import { useMemo } from "react";
import { z } from "zod";
import { useUsers } from "../hooks";
import { formatUserName } from "../utils";
import { BaseUserSchema } from "@/types";

interface UserOption {
  label: string;
  value: string;
}

type User = z.infer<typeof BaseUserSchema>;

/**
 * Hook to get filtered and formatted active user options for dropdowns
 * Returns only users who:
 * - Have at least a first name or last name
 * - Are active (is_active === true)
 */
export const useActiveUserOptions = (): UserOption[] => {
  const { users } = useUsers(true, true); // Include all users and deleted users to check is_active

  const userOptions = useMemo(() => {
    if (!users) return [];
    return (users as User[])
      .filter((user: User) => {
        // Filter out users where both first_name and last_name are null/undefined/empty
        const hasFirstName = user.first_name && user.first_name.trim() !== "";
        const hasLastName = user.last_name && user.last_name.trim() !== "";
        // Only include active users
        const isActive = user.is_active === true;
        return (hasFirstName || hasLastName) && isActive;
      })
      .map((user: User) => {
        const isDeleted =
          user.deleted_at !== null && user.deleted_at !== undefined && user.deleted_at !== "";
        const fullName = formatUserName(user);
        return {
          label: `${fullName}${isDeleted ? " (deleted)" : ""}`,
          value: fullName,
        };
      });
  }, [users]);

  return userOptions;
};

